import { db } from "../config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getBestCover } from "../../utils/coverUtils";
import {
  getOpenLibraryBookData,
  mergeBookData,
} from "../../services/openLibraryApi";
import { stripHtml } from "../../utils/textUtils";

const BOOKS_COLLECTION = "Books";

export const getBookById = async (googleBooksId) => {
  try {
    const bookRef = doc(db, BOOKS_COLLECTION, googleBooksId);
    const bookDoc = await getDoc(bookRef);

    if (bookDoc.exists()) {
      return { id: bookDoc.id, ...bookDoc.data() };
    }

    return null;
  } catch (error) {
    console.error("Error fetching book by ID:", error);
    throw error;
  }
};

export const saveBook = async (bookData) => {
  try {
    const bookRef = doc(db, BOOKS_COLLECTION, bookData.googleBooksId);
    const existingDoc = await getDoc(bookRef);

    if (existingDoc.exists()) {
      // Update existing book - don't set createdAt
      await setDoc(bookRef, {
        ...bookData,
        updatedAt: new Date().toISOString(),
      });
    } else {
      // Create new book - set both createdAt and updatedAt
      await setDoc(bookRef, {
        ...bookData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }

    return bookData;
  } catch (error) {
    console.error("Error saving book:", error);
    throw error;
  }
};

export const getOrCreateBook = async (
  googleBookData,
  preferredCoverUrl = null,
  forceUpdate = false
) => {
  try {
    const googleBooksId = googleBookData.id;

    // Check if book exists in our database
    const existingBook = await getBookById(googleBooksId);
    if (existingBook && !forceUpdate) {
      // If there's a preferred cover URL and it's different from the existing one,
      // update the book with the new cover URL
      if (preferredCoverUrl && existingBook.coverUrl !== preferredCoverUrl) {
        const updatedBook = {
          ...existingBook,
          coverUrl: preferredCoverUrl,
          coverSource: "manual",
        };
        await saveBook(updatedBook, true);
        return updatedBook;
      }
      return existingBook;
    }

    // Extract ISBN for Open Library lookup
    const isbn =
      googleBookData.volumeInfo.industryIdentifiers?.find(
        (id) => id.type === "ISBN_13" || id.type === "ISBN_10"
      )?.identifier || "";

    // Format Google Books data
    const googleData = {
      googleBooksId,
      title: googleBookData.volumeInfo.title,
      subtitle: googleBookData.volumeInfo.subtitle,
      authors: googleBookData.volumeInfo.authors || ["Unknown Author"],
      description: stripHtml(googleBookData.volumeInfo.description || ""),
      publishDate: googleBookData.volumeInfo.publishedDate?.split("-")[0] || "",
      genre: googleBookData.volumeInfo.categories?.[0] || "",
      rating: googleBookData.volumeInfo.averageRating || 0,
      totalRatings: googleBookData.volumeInfo.ratingsCount || 0,
      pages: googleBookData.volumeInfo.pageCount || 0,
      tags: googleBookData.volumeInfo.categories || [],
      language: googleBookData.volumeInfo.language || "",
      isbn: isbn,
    };

    // Fetch Open Library data for cross-reference (run in parallel with cover check)
    const [openLibraryData, coverResult] = await Promise.all([
      isbn ? getOpenLibraryBookData(isbn) : null,
      preferredCoverUrl
        ? { url: preferredCoverUrl, source: "manual" }
        : getBestCover(googleBookData.volumeInfo.imageLinks, isbn),
    ]);

    // Merge data from both sources
    const mergedData = mergeBookData(googleData, openLibraryData);

    // Format final book data
    const bookData = {
      googleBooksId,
      title: mergedData.title,
      subtitle: mergedData.subtitle || "",
      author:
        Array.isArray(mergedData.authors)
          ? mergedData.authors.join(", ")
          : mergedData.authors || "Unknown Author",
      coverUrl: coverResult.url,
      coverSource: coverResult.source,
      description: stripHtml(mergedData.description || ""),
      publishDate: mergedData.publishDate || "",
      genre: mergedData.genre || "",
      rating: mergedData.rating || 0,
      totalRatings: mergedData.totalRatings || 0,
      pages: mergedData.pages || 0,
      tags: mergedData.categories || [],
      language: googleData.language || "",
      isbn: isbn,
      dataSource: mergedData.dataSource || "google",
      openLibraryId: mergedData.openLibraryId || "",
    };

    // Save to our database with forceUpdate flag
    return await saveBook(bookData, forceUpdate);
  } catch (error) {
    console.error("Error in getOrCreateBook:", error);
    throw error;
  }
};

export const fetchBookByIsbn = async (isbn) => {
  try {
    const response = await fetch(
      `https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}`
    );
    const data = await response.json();

    if (data.items && data.items.length > 0) {
      const book = data.items[0].volumeInfo;
      return {
        id: data.items[0].id,
        title: book.title,
        authors: book.authors || [],
        thumbnail: book.imageLinks?.thumbnail || null,
        // Add any other fields you need
      };
    }
    return null;
  } catch (error) {
    console.error("Error fetching book by ISBN:", error);
    return null;
  }
};
